import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type { CreateExpense, UpdateExpense } from '@trellis/shared'

import { usePagedInfiniteQuery } from '@/hooks/usePagedInfiniteQuery'
import { pushEvent } from '@/lib/analytics'
import {
  assignTagsToExpense,
  billExpense,
  claimExpense,
  createBusinessExpense,
  createReimbursementExpense,
  deleteExpense,
  deleteReceipt,
  duplicateExpense,
  getExpense,
  getReceiptUrl,
  listExpenses,
  removeTagFromExpense,
  unclaimExpense,
  updateExpense,
  uploadReceipt,
  type ExpenseListParams,
} from '@/requests/expenses'

/**
 * Shared with the expenses list route's loader ({@link expensesInfiniteQueryOptions}) so
 * a prefetched loader cache entry and this hook's read land on the same query key. The
 * expenses list route prefetches all three list variants (business/reimbursement/all)
 * with this same factory, since {@link useExpensesQuery} is called three times per page
 * render regardless of the active tab.
 */
export function expensesInfiniteQueryOptions(params: ExpenseListParams = {}) {
  return infiniteQueryOptions({
    queryKey: ['expenses', params],
    queryFn: ({ pageParam }) =>
      listExpenses({ ...params, cursor: pageParam ?? params.cursor }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.pagination?.nextCursor ?? undefined,
    staleTime: 60_000,
  })
}

export function useExpensesQuery(params: ExpenseListParams = {}) {
  const limit = params.limit ?? 25

  const query = useInfiniteQuery(expensesInfiniteQueryOptions(params))

  return { ...query, ...usePagedInfiniteQuery(query, limit) }
}

export function useExpenseQuery(id: string) {
  return useQuery({
    queryKey: ['expenses', id],
    queryFn: () => getExpense(id),
    enabled: !!id,
  })
}

export function useCreateExpenseMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateExpense) => {
      const result =
        data.type === 'BUSINESS'
          ? await createBusinessExpense(data)
          : await createReimbursementExpense(data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      pushEvent('expense_created')
      await queryClient.invalidateQueries({ queryKey: ['expenses'] })
    },
    onError: () => pushEvent('form_submit_failed', { form_id: 'expense' }),
  })
}

export function useUpdateExpenseMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateExpense }) => {
      const result = await updateExpense(id, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] })
      await queryClient.invalidateQueries({ queryKey: ['expenses', variables.id] })
    },
  })
}

export function useDuplicateExpenseMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await duplicateExpense(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['expenses'] }),
        queryClient.invalidateQueries({ queryKey: ['expenses', result.data.id] }),
      ])
    },
  })
}

export function useDeleteExpenseMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await deleteExpense(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] })
    },
  })
}

export function useExpenseReceiptQuery(expenseId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['expenses', expenseId, 'receipt'],
    queryFn: () => getReceiptUrl(expenseId),
    enabled: enabled && !!expenseId,
    // Signed URLs expire after 15 minutes — refetch at 14 minutes to stay fresh
    staleTime: 14 * 60 * 1000,
  })
}

export function useUploadReceiptMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ expenseId, file }: { expenseId: string; file: File }) => {
      const result = await uploadReceipt(expenseId, file)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['expenses', variables.expenseId] })
      await queryClient.invalidateQueries({
        queryKey: ['expenses', variables.expenseId, 'receipt'],
      })
    },
  })
}

export function useBillExpenseMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      expenseId,
      invoiceId,
    }: {
      expenseId: string
      invoiceId: string
    }) => {
      const result = await billExpense(expenseId, invoiceId)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['expenses'] }),
        queryClient.invalidateQueries({ queryKey: ['invoices'] }),
      ])
    },
  })
}

export function useClaimExpenseMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await claimExpense(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] })
      await queryClient.invalidateQueries({ queryKey: ['expenses', id] })
    },
  })
}

export function useUnclaimExpenseMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await unclaimExpense(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] })
      await queryClient.invalidateQueries({ queryKey: ['expenses', id] })
    },
  })
}

export function useAssignExpenseTagsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ expenseId, tagIds }: { expenseId: string; tagIds: string[] }) =>
      assignTagsToExpense(expenseId, tagIds),
    onSuccess: async (_, { expenseId }) => {
      await queryClient.invalidateQueries({ queryKey: ['expenses', expenseId] })
    },
  })
}

export function useRemoveExpenseTagMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ expenseId, tagId }: { expenseId: string; tagId: string }) =>
      removeTagFromExpense(expenseId, tagId),
    onSuccess: async (_, { expenseId }) => {
      await queryClient.invalidateQueries({ queryKey: ['expenses', expenseId] })
    },
  })
}

export function useDeleteReceiptMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (expenseId: string) => {
      const result = await deleteReceipt(expenseId)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, expenseId) => {
      await queryClient.invalidateQueries({ queryKey: ['expenses', expenseId] })
      await queryClient.invalidateQueries({
        queryKey: ['expenses', expenseId, 'receipt'],
      })
    },
  })
}
